import type { CancelablePromise } from '@services/requests/core/CancelablePromise';
import { OpenAPIHousien } from '@services/requests/core/OpenAPI';
import { request as __request } from '@services/requests/core/request';
import type {
  CreateVistoryInput,
  PhotoUploadUrlResponse,
  PhotoViewUrlResponse,
  SendForApprovalInput,
  ShareVistoryInput,
  ShareVistoryResponse,
  UpdateVistoryInput,
  Vistory,
  VistoryListResponse,
} from '../types/vistory.types';

/** Typed client for vistory CRUD, photo upload, and approval operations. */
export class VistoryService {
  public static createVistory(
    agencyId: string,
    leaseId: string,
    data: CreateVistoryInput,
  ): CancelablePromise<Vistory> {
    return __request(OpenAPIHousien, {
      method: 'POST',
      url: '/agency/{agencyId}/leases/{leaseId}/vistories',
      path: { agencyId, leaseId },
      body: data,
      mediaType: 'application/json',
    });
  }

  public static getVistory(agencyId: string, vistoryId: string): CancelablePromise<Vistory> {
    return __request(OpenAPIHousien, {
      method: 'GET',
      url: '/agency/{agencyId}/vistories/{vistoryId}',
      path: { agencyId, vistoryId },
    });
  }

  public static updateVistory(
    agencyId: string,
    vistoryId: string,
    data: UpdateVistoryInput,
  ): CancelablePromise<Vistory> {
    return __request(OpenAPIHousien, {
      method: 'PUT',
      url: '/agency/{agencyId}/vistories/{vistoryId}',
      path: { agencyId, vistoryId },
      body: data,
      mediaType: 'application/json',
    });
  }

  public static listVistories(
    agencyId: string,
    leaseId: string,
  ): CancelablePromise<VistoryListResponse> {
    return __request(OpenAPIHousien, {
      method: 'GET',
      url: '/agency/{agencyId}/leases/{leaseId}/vistories',
      path: { agencyId, leaseId },
    });
  }

  public static getPhotoUploadUrl(
    agencyId: string,
    vistoryId: string,
    body: { filename: string; content_type: string },
  ): CancelablePromise<PhotoUploadUrlResponse> {
    return __request(OpenAPIHousien, {
      method: 'POST',
      url: '/agency/{agencyId}/vistories/{vistoryId}/photos/upload-url',
      path: { agencyId, vistoryId },
      body,
      mediaType: 'application/json',
    });
  }

  public static addPhotoMetadata(
    agencyId: string,
    vistoryId: string,
    // `photo_key`, not `key`: the route reads body["photo_key"] and a
    // `key` payload raised a KeyError before the photo was ever recorded.
    body: { photo_key: string; filename: string; content_type: string; room_id?: string },
  ): CancelablePromise<Vistory> {
    return __request(OpenAPIHousien, {
      method: 'POST',
      url: '/agency/{agencyId}/vistories/{vistoryId}/photos',
      path: { agencyId, vistoryId },
      body,
      mediaType: 'application/json',
    });
  }

  public static getPhotoViewUrl(
    agencyId: string,
    vistoryId: string,
    photoKey: string,
  ): CancelablePromise<PhotoViewUrlResponse> {
    return __request(OpenAPIHousien, {
      method: 'GET',
      url: '/agency/{agencyId}/vistories/{vistoryId}/photos/{photoKey}/view-url',
      path: { agencyId, vistoryId, photoKey },
    });
  }

  public static sendForApproval(
    agencyId: string,
    vistoryId: string,
    data: SendForApprovalInput,
  ): CancelablePromise<Vistory> {
    return __request(OpenAPIHousien, {
      method: 'POST',
      url: '/agency/{agencyId}/vistories/{vistoryId}/send-for-approval',
      path: { agencyId, vistoryId },
      body: data,
      mediaType: 'application/json',
    });
  }

  public static shareVistory(
    agencyId: string,
    vistoryId: string,
    data: ShareVistoryInput,
  ): CancelablePromise<ShareVistoryResponse> {
    return __request(OpenAPIHousien, {
      method: 'POST',
      url: '/agency/{agencyId}/vistories/{vistoryId}/share',
      path: { agencyId, vistoryId },
      body: data,
      mediaType: 'application/json',
    });
  }

  public static generateReport(
    agencyId: string,
    vistoryId: string,
  ): CancelablePromise<{ draft: string }> {
    return __request(OpenAPIHousien, {
      method: 'POST',
      url: '/agency/{agencyId}/vistories/{vistoryId}/generate-report',
      path: { agencyId, vistoryId },
    });
  }
}
